import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getPortalStatus,
  PortalApiError,
} from '../services/portalApi';
import { PortalSessionContext } from './PortalSessionContext';
import type {
  PortalSessionStatus,
  PortalSessionValue,
} from './portalSessionTypes';
import { useAuth } from './useAuth';

interface SessionState {
  status: PortalSessionStatus;
  response: PortalSessionValue['response'];
  message: string | null;
}

const signedOutState: SessionState = {
  status: 'signedOut',
  response: null,
  message: null,
};

export function PortalSessionProvider({
  children,
}: PropsWithChildren) {
  const {
    isAuthenticated,
    isLoading: isAuthenticationLoading,
    getAccessToken,
  } = useAuth();

  const [state, setState] =
    useState<SessionState>(signedOutState);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const refresh = useCallback((): void => {
    setRefreshVersion((current) => current + 1);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    async function loadPortalSession(): Promise<void> {
      if (isAuthenticationLoading) {
        setState({
          status: 'loading',
          response: null,
          message: null,
        });
        return;
      }

      setState({
        status: 'loading',
        response: null,
        message: null,
      });

      try {
        const accessToken = isAuthenticated
          ? await getAccessToken()
          : '';
        const response = await getPortalStatus(
          accessToken,
          abortController.signal,
        );

        setState({
          status: 'ready',
          response,
          message: null,
        });
      } catch (error) {
        if (
          error instanceof DOMException &&
          error.name === 'AbortError'
        ) {
          return;
        }

        if (
          error instanceof PortalApiError &&
          error.status === 401
        ) {
          setState(signedOutState);
          return;
        }

        if (
          error instanceof PortalApiError &&
          error.status === 403
        ) {
          setState({
            status: 'forbidden',
            response: null,
            message:
              'Your portal account is inactive, blocked, or does not have access.',
          });
          return;
        }

        const message =
          error instanceof Error
            ? error.message
            : 'The portal profile could not be loaded.';

        setState({
          status: 'error',
          response: null,
          message,
        });
      }
    }

    void loadPortalSession();

    return () => {
      abortController.abort();
    };
  }, [
    getAccessToken,
    isAuthenticated,
    isAuthenticationLoading,
    refreshVersion,
  ]);

  const value = useMemo<PortalSessionValue>(
    () => ({
      status: state.status,
      user: state.response?.user ?? null,
      response: state.response,
      message: state.message,
      refresh,
    }),
    [refresh, state],
  );

  return (
    <PortalSessionContext.Provider value={value}>
      {children}
    </PortalSessionContext.Provider>
  );
}
